import {ProblemChange} from '@types';
import {Table, OverlayTrigger, Popover} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router';

const replaceSuggestionLink = (text: string): string => {
    return text.replace(/\(From suggestion #(\d+)\)/g, (_, id) => {
        return `<a class="link" href="/suggestions/${id}">(From suggestion #${id})</a>`;
    });
};

const renderPopover = (title: string, content: string, label: string) => (
    <OverlayTrigger
        trigger="click"
        rootClose
        placement="right"
        overlay={
            <Popover>
                <Popover.Header as="h3">{title}</Popover.Header>
                <Popover.Body dangerouslySetInnerHTML={{__html: content}} />
            </Popover>
        }>
        <kbd className="bg-secondary cursor-pointer mx-0.5">{label}</kbd>
    </OverlayTrigger>
);

const Diff = ({changeValue}: {changeValue: string | object}) => {
    const {t} = useTranslation();
    // Flag model
    if (
        changeValue &&
        typeof changeValue === 'object' &&
        'user' in changeValue &&
        typeof changeValue.user === 'object' &&
        changeValue.user !== null &&
        'name' in changeValue.user &&
        'description' in changeValue &&
        'flag' in changeValue &&
        'flagName' in changeValue
    ) {
        return renderPopover(
            t('problem_draft.flag'),
            `<strong>${t('common.author')}:</strong> <a href="/users/${changeValue.user.name}" target="_blank">${changeValue.user.name}</a><br/><strong>${t(
                'problem_draft.description'
            )}:</strong> ${changeValue.description}`,
            `${changeValue.flagName}`
        );
    }

    // Collection model
    if (
        changeValue &&
        typeof changeValue === 'object' &&
        'id' in changeValue &&
        'name' in changeValue &&
        'description' in changeValue
    ) {
        return renderPopover(
            t('problem_draft.collection'),
            `<strong>${t('common.name')}:</strong> <a href="/collection/${changeValue.id}" target="_blank">${changeValue.name}</a><br/><strong>${t(
                'problem_draft.description'
            )}:</strong> ${changeValue.description}`,
            `${changeValue.name}`
        );
    }

    if (typeof changeValue === 'string') {
        return <kbd className="bg-secondary mx-0.5">{changeValue}</kbd>;
    }
    return null;
};

export const ChangeHistoryTable = ({changes}: {changes: ProblemChange[]}) => {
    const {t} = useTranslation();

    return (
        <Table striped responsive size="sm">
            <thead className="text-sm">
                <tr>
                    <th>
                        {t('problem_draft.change_history_table_cols.initiator')}
                    </th>
                    <th>
                        {t('problem_draft.change_history_table_cols.operation')}
                    </th>
                    <th>
                        {t('problem_draft.change_history_table_cols.property')}
                    </th>
                    <th style={{minWidth: 100}}>
                        {t(
                            'problem_draft.change_history_table_cols.visual_diff'
                        )}
                    </th>
                    <th>
                        {t('problem_draft.change_history_table_cols.comment')}
                    </th>
                    <th>{t('problem_draft.change_history_table_cols.date')}</th>
                    <th>{t('problem_draft.change_history_table_cols.diff')}</th>
                </tr>
            </thead>
            <tbody className="text-xs">
                {changes &&
                    changes.map((change, idx) => (
                        <tr key={idx} className="align-middle">
                            <td>
                                {change.initiator ? (
                                    <Link
                                        to={`/users/${change.initiator.name}`}
                                        className="link-secondary text-decoration-none">
                                        {change.initiator.name}
                                    </Link>
                                ) : (
                                    '—'
                                )}
                            </td>
                            <td>{change.operation}</td>
                            <td>{change.property ?? '—'}</td>
                            <td>
                                {change.property === 'sgf' ? (
                                    <Link
                                        className="link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        to={`/tools/sgf/difftool?sgf1=${change.oldValue}&sgf2=${change.newValue}`}>
                                        {t(
                                            'problem_draft.change_history_table_cols.visual_diff'
                                        )}
                                    </Link>
                                ) : (
                                    '—'
                                )}
                            </td>
                            <td
                                dangerouslySetInnerHTML={{
                                    __html: replaceSuggestionLink(
                                        change.comment ?? ''
                                    ),
                                }}></td>
                            <td>
                                {new Date(change.changedAt).toLocaleString()}
                            </td>
                            <td>
                                {change.property ? (
                                    change.addedValues.length > 0 ||
                                    change.removedValues.length > 0 ? (
                                        <>
                                            {change.addedValues.length > 0 && (
                                                <>
                                                    <span>
                                                        {t(
                                                            'problem_draft.added'
                                                        )}{' '}
                                                    </span>
                                                    {change.addedValues.map(
                                                        (val, i) => (
                                                            <Diff
                                                                key={
                                                                    'added-values' +
                                                                    i
                                                                }
                                                                changeValue={
                                                                    val
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </>
                                            )}
                                            {change.removedValues.length >
                                                0 && (
                                                <>
                                                    <span>
                                                        {' '}
                                                        {t(
                                                            'problem_draft.removed'
                                                        )}{' '}
                                                    </span>
                                                    {change.removedValues.map(
                                                        (val, i) => (
                                                            <Diff
                                                                key={
                                                                    'removed-values' +
                                                                    i
                                                                }
                                                                changeValue={
                                                                    val
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <span>
                                                {t('problem_draft.changed')}{' '}
                                            </span>
                                            {[
                                                ...(Array.isArray(
                                                    change.oldValue
                                                )
                                                    ? change.oldValue
                                                    : [change.oldValue]),
                                            ].map((val, i) => (
                                                <Diff
                                                    key={`old-value-${i}`}
                                                    changeValue={val}
                                                />
                                            ))}
                                            <span>
                                                {' '}
                                                {t('problem_draft.to')}{' '}
                                            </span>
                                            {[
                                                ...(Array.isArray(
                                                    change.newValue
                                                )
                                                    ? change.newValue
                                                    : [change.newValue]),
                                            ].map((val, i) => (
                                                <Diff
                                                    key={`new-value-${i}`}
                                                    changeValue={val}
                                                />
                                            ))}
                                        </>
                                    )
                                ) : (
                                    '—'
                                )}
                            </td>
                        </tr>
                    ))}
            </tbody>
        </Table>
    );
};
